// The notifications drawer: briefs, reminders, "needs you now" breakthroughs and account health,
// newest first. Opening one marks it seen and goes to it; Dismiss hides it (with undo); "Mark all
// seen" quiets the bell. A dialog, so Esc closes it and the page behind stays still.
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AlarmClock, Bell, CircleAlert, Sunrise, X, Zap } from "lucide-react";
import type { ComponentType } from "react";
import { toast } from "sonner";
import type { NotificationView } from "@shared/api";
import { cn } from "@web/lib/cn";
import { deferWithUndo } from "@web/lib/deferredUndo";
import { notifyApi } from "@web/lib/notifyApi";
import { notifyKeys, useNotifications } from "@web/lib/notifyQueries";
import { taskQuery } from "@web/lib/tasksQueries";
import { formatAgo, formatFull } from "@web/lib/time";
import { useNow } from "@web/lib/useNow";
import { Button } from "@web/ui/Button";
import { Modal } from "@web/ui/Modal";
import { tasksSearchFor } from "@web/tasks/tasksSearch";
import { short, taskViewFor } from "@web/tasks/wording";
import { notifyStore, useDrawerOpen, useHiddenNotifications } from "./notifyStore";
import { NOTIFICATION_KIND_LABEL, notificationTarget } from "./wording";

const KIND_ICON: Record<NotificationView["kind"], ComponentType<{ className?: string }>> = {
  brief: Sunrise,
  breakthrough: Zap,
  reminder: AlarmClock,
  health: CircleAlert,
  sync_gap: CircleAlert,
};

function Item({
  n,
  now,
  onOpen,
  onDismiss,
}: {
  n: NotificationView;
  now: number;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const Icon = KIND_ICON[n.kind] ?? Bell;
  const unseen = !n.seenAt;
  return (
    <li className="group relative flex items-start gap-3 px-4 py-3 hover:bg-sunk/50">
      <Icon
        aria-hidden
        className={cn(
          "mt-0.5 size-4 shrink-0",
          n.severity === "attention" ? "text-note-ink" : "text-muted",
        )}
      />
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        <span className="flex items-center gap-2">
          <span className="mm-kicker !text-[10px]">{NOTIFICATION_KIND_LABEL[n.kind]}</span>
          {unseen ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-accent-text">
              <span aria-hidden className="size-1.5 rounded-full bg-accent" />
              new
            </span>
          ) : null}
          <time className="ml-auto text-xs text-faint" title={formatFull(n.createdAt)}>
            {formatAgo(n.createdAt, now)}
          </time>
        </span>
        <span className={cn("block break-words text-ink", unseen && "font-medium")}>{n.title}</span>
        {n.detail ? <span className="block text-sm break-words text-muted">{n.detail}</span> : null}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={`Dismiss: ${n.title}`}
        title="Dismiss"
        className="shrink-0 rounded-md p-1 text-faint hover:bg-sunk hover:text-ink"
      >
        <X aria-hidden className="size-4" />
      </button>
    </li>
  );
}

export function NotificationsDrawer({ enabled }: { enabled: boolean }) {
  const open = useDrawerOpen();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const now = useNow(60_000);
  const list = useNotifications(false, enabled && open);
  const hidden = useHiddenNotifications();
  const items = (list.data ?? []).filter((n) => !hidden.has(n.id));
  const unseen = items.filter((n) => !n.seenAt).length;
  const close = () => notifyStore.setOpen(false);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: notifyKeys.all });

  const markSeen = (n: NotificationView) => {
    if (n.seenAt) return;
    void notifyApi.seen(n.id).then(refresh, () => undefined);
  };

  const openItem = async (n: NotificationView) => {
    markSeen(n);
    close();
    const target = notificationTarget(n);
    if (!target) return;
    switch (target.kind) {
      case "brief":
        void navigate({ to: "/brief/$briefId", params: { briefId: target.briefId } });
        return;
      case "card":
        void navigate({ to: "/feed/cards/$cardId", params: { cardId: target.cardId } });
        return;
      case "accounts":
        void navigate({ to: "/accounts" });
        return;
      case "task": {
        const task = await queryClient.fetchQuery(taskQuery(target.taskId)).catch(() => null);
        const view = task ? taskViewFor(task, now) : "today";
        void navigate({ to: "/tasks", search: tasksSearchFor(view, target.taskId) });
        return;
      }
    }
  };

  const dismiss = (n: NotificationView) =>
    deferWithUndo({
      message: `Dismissed · ${short(n.title)}`,
      apply: () => notifyStore.hide(n.id),
      revert: () => notifyStore.unhide(n.id),
      commit: async () => {
        try {
          await notifyApi.dismiss(n.id);
          refresh();
        } catch {
          notifyStore.unhide(n.id);
          toast("Couldn't dismiss that notification. It's still in the list.");
        }
      },
      doneMessage: "Back in your notifications.",
    });

  const seenAll = async () => {
    try {
      await notifyApi.seenAll();
    } catch {
      toast("Couldn't update that just now.");
    } finally {
      refresh();
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      label="Notifications"
      // No display utility here: a closed <dialog> must keep the browser's display: none.
      className="m-0 ml-auto h-dvh max-h-dvh w-[min(420px,100vw)] max-w-full rounded-none border-y-0 border-r-0"
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <h2 className="flex-1 text-lg">Notifications</h2>
          {unseen ? (
            <Button size="sm" variant="ghost" onClick={() => void seenAll()}>
              Mark all seen
            </Button>
          ) : null}
          <button
            type="button"
            onClick={close}
            aria-label="Close notifications"
            className="rounded-md p-1.5 text-muted hover:bg-sunk hover:text-ink"
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {list.isPending ? (
            <div aria-hidden className="m-4 h-24 rounded-xl bg-sunk" />
          ) : list.isError && !list.data ? (
            <p className="px-6 py-10 text-center text-muted">
              Couldn’t load notifications. Nothing is lost.
            </p>
          ) : !items.length ? (
            <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
              <p className="font-serif text-xl text-ink">Nothing new</p>
              <p className="max-w-xs text-sm text-muted">
                Briefs, reminders and anything that needs you now show up here.
              </p>
            </div>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {items.map((n) => (
                <Item
                  key={n.id}
                  n={n}
                  now={now}
                  onOpen={() => void openItem(n)}
                  onDismiss={() => dismiss(n)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
